// apps/web/src/pages/BillingBatchPage.tsx
//
// Manual batch invoice generation:
//   1. Pick a period (default = current month)
//   2. See active subscriptions with next_billing_date <= period
//   3. Multi-select + preview + generate
//   4. See recent batches and drill into items
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, AlertTriangle, Play, ListChecks, History, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/services/supabase';
import {
  listSubscriptions, listBillingProducts, runBatch, listBatches, listBatchItems,
  type BillingSubscription, type BillingProduct, type InvoiceBatch, type InvoiceBatchItem,
} from '@/services/billing';

interface SubRow extends BillingSubscription {
  account_name?: string;
  site_name?: string | null;
  product_name?: string;
  unit_price: number;
  total: number;
}

function currentPeriodStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export function BillingBatchPage() {
  const { hasRole } = usePermissions();
  const canManage = hasRole('admin') || hasRole('super_admin');

  const [period, setPeriod]       = useState(currentPeriodStr());
  const [rows, setRows]           = useState<SubRow[]>([]);
  const [selected, setSelected]   = useState<Set<string>>(new Set());
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [running, setRunning]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [previewOpen, setPreview] = useState(false);
  const [batches, setBatches]     = useState<InvoiceBatch[]>([]);
  const [batchDetail, setBatchDetail] = useState<{ batch: InvoiceBatch; items: InvoiceBatchItem[] } | null>(null);

  useEffect(() => { void refresh(); }, [period]);

  async function refresh() {
    setLoading(true);
    setSelected(new Set());
    try {
      const [subs, products, recentBatches] = await Promise.all([
        listSubscriptions({ dueOnly: true, period }),
        listBillingProducts(),
        listBatches(10),
      ]);
      const productMap = new Map<string, BillingProduct>();
      for (const p of products) productMap.set(p.id, p);

      // Fetch related accounts + sites
      const accIds  = [...new Set(subs.map(s => s.account_id))];
      const siteIds = [...new Set(subs.map(s => s.site_id).filter(Boolean) as string[])];
      const [accRes, siteRes] = await Promise.all([
        accIds.length ? supabase.from('accounts').select('id,name').in('id', accIds) : Promise.resolve({ data: [] as { id: string; name: string }[] }),
        siteIds.length ? supabase.from('alarm_sites').select('id,name').in('id', siteIds) : Promise.resolve({ data: [] as { id: string; name: string }[] }),
      ]);
      const accMap  = new Map((accRes.data ?? []).map(a => [a.id, a.name]));
      const siteMap = new Map((siteRes.data ?? []).map(s => [s.id, s.name]));

      const enriched: SubRow[] = subs.map(s => {
        const prod = productMap.get(s.product_id);
        const unit = s.unit_price_override ?? prod?.default_unit_price ?? 0;
        return {
          ...s,
          account_name: accMap.get(s.account_id) ?? '—',
          site_name: s.site_id ? siteMap.get(s.site_id) ?? null : null,
          product_name: prod?.name ?? '—',
          unit_price: unit,
          total: unit * s.quantity,
        };
      });
      setRows(enriched);
      setBatches(recentBatches);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar');
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.account_name ?? '').toLowerCase().includes(q) ||
      (r.site_name ?? '').toLowerCase().includes(q) ||
      (r.product_name ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const selectedRows = filtered.filter(r => selected.has(r.id));
  const totalAmount = selectedRows.reduce((s, r) => s + r.total, 0);

  function toggleAll() {
    if (filtered.every(r => selected.has(r.id))) {
      const next = new Set(selected);
      filtered.forEach(r => next.delete(r.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filtered.forEach(r => next.add(r.id));
      setSelected(next);
    }
  }

  async function execute() {
    setRunning(true);
    setError(null);
    try {
      const result = await runBatch({
        billing_period: period,
        subscription_ids: [...selected],
        mode: 'manual',
        notes: `Manual batch — ${selected.size} suscripciones`,
      });
      setPreview(false);
      await refresh();
      // Auto-open the new batch
      try {
        const items = await listBatchItems(result.batch_id);
        const fresh = await listBatches(10);
        const batch = fresh.find(b => b.id === result.batch_id) ?? null;
        if (batch) setBatchDetail({ batch, items });
      } catch { /* ignore */ }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error en el batch');
    } finally {
      setRunning(false);
    }
  }

  async function openBatch(b: InvoiceBatch) {
    try {
      const items = await listBatchItems(b.id);
      setBatchDetail({ batch: b, items });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  }

  if (!canManage) {
    return (
      <div className="container mx-auto p-6">
        <Card><CardContent className="p-6 text-muted-foreground">
          Solo administradores pueden generar facturación por lote.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-7xl">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <ListChecks className="w-6 h-6" /> Facturación por lote
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Generá facturas en borrador para suscripciones activas. Las facturas quedan listas
          para aprobar y emitir el DTE.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-rose-500/10 text-rose-700 border border-rose-500/30 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Período</Label>
              <Input type="month" value={period.slice(0, 7)}
                onChange={e => setPeriod(`${e.target.value}-01`)} className="w-40" />
            </div>
            <div className="flex-1 min-w-60">
              <Label className="flex items-center gap-1"><Search className="w-3 h-3" /> Buscar</Label>
              <Input placeholder="Cuenta, sitio o producto..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <Button variant="outline" onClick={toggleAll}>
              {filtered.length > 0 && filtered.every(r => selected.has(r.id)) ? 'Quitar todos' : 'Seleccionar todos'}
            </Button>
            <Button disabled={selected.size === 0} onClick={() => setPreview(true)}>
              <Play className="w-4 h-4 mr-1" /> Facturar {selected.size}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">No hay suscripciones que facturar este período.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2 font-medium">Cuenta</th>
                    <th className="px-3 py-2 font-medium">Sitio</th>
                    <th className="px-3 py-2 font-medium">Producto</th>
                    <th className="px-3 py-2 font-medium text-right">Cant.</th>
                    <th className="px-3 py-2 font-medium text-right">P. Unit.</th>
                    <th className="px-3 py-2 font-medium text-right">Total</th>
                    <th className="px-3 py-2 font-medium">Próx.</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} className={`border-t ${selected.has(r.id) ? 'bg-primary/5' : ''}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={selected.has(r.id)}
                          onChange={e => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(r.id); else next.delete(r.id);
                            setSelected(next);
                          }} />
                      </td>
                      <td className="px-3 py-2">{r.account_name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.site_name ?? '—'}</td>
                      <td className="px-3 py-2">{r.product_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.quantity}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.unit_price.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-mono font-medium">{r.total.toFixed(2)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.next_billing_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent batches */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><History className="w-5 h-5" /> Lotes recientes</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {batches.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Aún no hay lotes ejecutados.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Fecha</th>
                  <th className="px-3 py-2 font-medium">Período</th>
                  <th className="px-3 py-2 font-medium">Origen</th>
                  <th className="px-3 py-2 font-medium">Estado</th>
                  <th className="px-3 py-2 font-medium text-right">OK / Falló / Saltó</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.id} className="border-t">
                    <td className="px-3 py-2">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2">{b.billing_period}</td>
                    <td className="px-3 py-2 capitalize">{b.origin}</td>
                    <td className="px-3 py-2">
                      <Badge variant={b.status === 'completed' ? 'secondary' : b.status.includes('error') || b.status === 'failed' ? 'destructive' : 'outline'}>
                        {b.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      <span className="text-emerald-600">{b.success_count}</span>
                      {' / '}
                      <span className="text-rose-600">{b.failed_count}</span>
                      {' / '}
                      <span className="text-muted-foreground">{b.skipped_count}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openBatch(b)}>Ver</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreview}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar facturación</DialogTitle>
            <DialogDescription>
              Vas a crear facturas en <strong>borrador</strong> para las suscripciones seleccionadas.
              No se emiten al SAT hasta que las apruebes.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong>Período:</strong> {period}</div>
            <div><strong>Suscripciones:</strong> {selected.size}</div>
            <div><strong>Cuentas distintas:</strong> {new Set(selectedRows.map(r => r.account_id)).size}</div>
            <div><strong>Total estimado:</strong> GTQ {totalAmount.toFixed(2)}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(false)} disabled={running}>Cancelar</Button>
            <Button onClick={execute} disabled={running}>
              {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              Generar facturas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch detail */}
      <Dialog open={!!batchDetail} onOpenChange={(o) => { if (!o) setBatchDetail(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Lote {batchDetail?.batch.id.slice(0, 8)}</DialogTitle>
            <DialogDescription>
              {batchDetail?.batch.billing_period} — {batchDetail?.batch.status}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Estado</th>
                  <th className="px-3 py-2 font-medium">Cuenta</th>
                  <th className="px-3 py-2 font-medium">Monto</th>
                  <th className="px-3 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {batchDetail?.items.map(it => (
                  <tr key={it.id} className="border-t">
                    <td className="px-3 py-2">
                      <Badge variant={it.status === 'success' ? 'secondary' : it.status === 'failed' ? 'destructive' : 'outline'}>
                        {it.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{it.account_id.slice(0, 8)}</td>
                    <td className="px-3 py-2 font-mono">{it.amount?.toFixed(2) ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-rose-600 max-w-md truncate" title={it.error_message ?? ''}>
                      {it.error_message ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
